import React, { createContext, useContext, useState, useEffect } from 'react'
import { supabase } from '../../database/supabaseclient.js'

// App Context for global state
const AppContext = createContext()

export const useAppContext = () => {
    const context = useContext(AppContext)
    if (!context) {
        throw new Error('useAppContext must be used within AppProvider')
    }
    return context
}

export const AppProvider = ({ children }) => {
    const [user, setUser] = useState(null)
    const [userProfile, setUserProfile] = useState(null)
    const [currentShop, setCurrentShop] = useState(null)
    const [availableShops, setAvailableShops] = useState([])
    const [availableWarehouses, setAvailableWarehouses] = useState([])
    const [cart, setCart] = useState([])
    const [recentSearches, setRecentSearches] = useState([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        console.log('AppContext: useEffect triggered')
        // Get current user
        const getCurrentUser = async () => {
            try {
                console.log('AppContext: Starting to get current user...')
                const { data: { user }, error: userError } = await supabase.auth.getUser()

                if (userError) {
                    console.error('Error getting user:', userError)
                    setLoading(false)
                    return
                }

                console.log('AppContext: User found:', user?.email, 'ID:', user?.id)
                setUser(user)

                if (user) {
                    console.log('Getting profile for user:', user.id)
                    console.log('User object:', user)
                    // Get user profile
                    const { data: profile, error: profileError } = await supabase
                        .from('profiles')
                        .select('*, warehouses!assigned_shop_id(*)')
                        .eq('id', user.id)
                        .single()

                    if (profileError) {
                        console.error('Error getting profile:', profileError)
                        console.error('Profile error details:', {
                            message: profileError.message,
                            details: profileError.details,
                            hint: profileError.hint
                        })
                        setLoading(false)
                        return
                    } else {
                        console.log('Profile loaded:', profile)
                        console.log('Profile details:', {
                            id: profile?.id,
                            email: profile?.email,
                            role: profile?.role,
                            full_name: profile?.full_name
                        })
                    }

                    setUserProfile(profile)

                    if (profile?.role === 'admin') {
                        console.log('User is admin, loading all shops and warehouses')
                        console.log('Admin profile details:', {
                            id: profile.id,
                            email: profile.email,
                            role: profile.role,
                            assigned_shop_id: profile.assigned_shop_id,
                            full_name: profile.full_name
                        })

                        // Admin: Get all shops and warehouses
                        const { data: shops, error: shopsError } = await supabase
                            .from('warehouses')
                            .select('*')
                            .eq('type', 'shop')
                            .order('name')

                        const { data: warehouses, error: warehousesError } = await supabase
                            .from('warehouses')
                            .select('*')
                            .eq('type', 'warehouse')
                            .order('name')

                        if (shopsError) console.error('Error loading shops:', shopsError)
                        if (warehousesError) console.error('Error loading warehouses:', warehousesError)

                        console.log('Shops loaded:', shops?.length || 0)
                        console.log('Warehouses loaded:', warehouses?.length || 0)
                        console.log('Available shops:', shops)
                        console.log('Available warehouses:', warehouses)

                        setAvailableShops(shops || [])
                        setAvailableWarehouses(warehouses || [])

                        // For admin, use the assigned shop from profile if available, otherwise use first available shop
                        if (profile?.assigned_shop_id) {
                            console.log('Admin has assigned shop ID:', profile.assigned_shop_id)
                            // Find the assigned shop in the loaded shops
                            const assignedShop = shops?.find(shop => shop.id === profile.assigned_shop_id)
                            console.log('Found assigned shop:', assignedShop)
                            if (assignedShop) {
                                setCurrentShop(assignedShop)
                                console.log('Admin current shop set to assigned shop:', assignedShop.name)
                            } else if (shops && shops.length > 0) {
                                setCurrentShop(shops[0])
                                console.log('Admin assigned shop not found, using first available:', shops[0].name)
                            } else {
                                console.log('No shops available, admin will work without specific shop assignment')
                                setCurrentShop(null)
                            }
                        } else if (shops && shops.length > 0) {
                            console.log('Admin has no assigned shop, using first available:', shops[0].name)
                            setCurrentShop(shops[0])
                        } else {
                            console.log('No shops available, admin will work without specific shop assignment')
                            setCurrentShop(null)
                        }

                        console.log('Final admin state:', {
                            currentShop: currentShop,
                            availableShops: shops?.length || 0,
                            availableWarehouses: warehouses?.length || 0
                        })
                    } else {
                        // Shop seller: Use assigned shop
                        console.log('User is shop seller, checking assigned shop:', profile?.assigned_shop_id)
                        if (profile?.assigned_shop_id) {
                            // Get the assigned shop details
                            const { data: assignedShop, error: shopError } = await supabase
                                .from('warehouses')
                                .select('*')
                                .eq('id', profile.assigned_shop_id)
                                .single()

                            if (shopError) {
                                console.error('Error loading assigned shop:', shopError)
                                console.error('Shop error details:', {
                                    message: shopError.message,
                                    details: shopError.details,
                                    hint: shopError.hint,
                                    assigned_shop_id: profile.assigned_shop_id
                                })

                                // Try to find any available shop as fallback
                                const { data: fallbackShop, error: fallbackError } = await supabase
                                    .from('warehouses')
                                    .select('*')
                                    .eq('type', 'shop')
                                    .order('name')
                                    .limit(1)
                                    .single()

                                if (fallbackError) {
                                    console.error('Error loading fallback shop:', fallbackError)
                                } else {
                                    console.log('Using fallback shop:', fallbackShop)
                                    setCurrentShop(fallbackShop)
                                }
                            } else {
                                console.log('Assigned shop loaded:', assignedShop)
                                if (assignedShop.type === 'shop') {
                                    setCurrentShop(assignedShop)
                                } else {
                                    console.warn('Assigned warehouse is not a shop (type:', assignedShop.type, ')')
                                    // Try to find a proper shop
                                    const { data: properShop, error: properShopError } = await supabase
                                        .from('warehouses')
                                        .select('*')
                                        .eq('type', 'shop')
                                        .order('name')
                                        .limit(1)
                                        .single()

                                    if (!properShopError && properShop) {
                                        console.log('Using proper shop instead:', properShop)
                                        setCurrentShop(properShop)
                                    }
                                }
                            }
                        } else {
                            console.warn('Shop seller has no assigned shop')
                            // Try to find any available shop
                            const { data: anyShop, error: anyShopError } = await supabase
                                .from('warehouses')
                                .select('*')
                                .eq('type', 'shop')
                                .order('name')
                                .limit(1)
                                .single()

                            if (!anyShopError && anyShop) {
                                console.log('Found available shop for user without assignment:', anyShop)
                                setCurrentShop(anyShop)
                            }
                        }
                    }
                } else {
                    console.log('No user found, redirecting to login...')
                }
            } catch (error) {
                console.error('Unexpected error in getCurrentUser:', error)
            } finally {
                setLoading(false)
            }
        }

        // Add timeout to prevent infinite loading
        const timeoutId = setTimeout(() => {
            console.warn('AppContext: Loading timeout reached')
            setLoading(false)
        }, 10000) // 10 seconds timeout

        getCurrentUser()

        // Listen for auth changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
            console.log('Auth state changed:', event, session?.user?.email)
            if (event === 'SIGNED_OUT') {
                setUser(null)
                setUserProfile(null)
                setCurrentShop(null)
                setLoading(false)
            } else if (session?.user) {
                setUser(session.user)
                getCurrentUser()
            }
        })

        return () => {
            clearTimeout(timeoutId)
            subscription?.unsubscribe()
        }
    }, [])

    const addToRecentSearches = (search) => {
        if (search && !recentSearches.includes(search)) {
            const newRecent = [search, ...recentSearches.slice(0, 4)]
            setRecentSearches(newRecent)
            localStorage.setItem('fastparts_recent_searches', JSON.stringify(newRecent))
        }
    }

    const value = {
        user,
        userProfile,
        currentShop,
        availableShops,
        availableWarehouses,
        cart,
        recentSearches,
        loading,
        setUser,
        setUserProfile,
        setCurrentShop,
        setCart,
        addToRecentSearches
    }

    return <AppContext.Provider value={value}>{children}</AppContext.Provider>
} 